import { Box, Typography, Stack, Collapse, ConfirmationDialog, UserAvatar } from 'src/components';
import { useState, useEffect } from 'react';
import { deleteComment, toggleCommentUpvote } from 'src/utils';
import CommentForm from './comment-form';
import { useIsMobile, useSnackbar } from 'src/hooks';
import CommentActions from './comment-actions';

export default function CommentItem({ comment, allComments, currentUser, onCommentUpdate, depth = 0, initialRepliesCollapsed }) {
    const [showReplyForm, setShowReplyForm] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [repliesCollapsed, setRepliesCollapsed] = useState(initialRepliesCollapsed);

    const isMobile = useIsMobile();
    const { openSnackbar } = useSnackbar();

    const replies = allComments.filter(c => c.parentCommentId === comment.id);
    const isAuthor = currentUser?.id === comment.userId;
    const isNested = !!comment.parentCommentId;
    const hasUpvoted = comment.upvoters?.includes(currentUser?.id);

    useEffect(() => {
        setRepliesCollapsed(initialRepliesCollapsed);
    }, [initialRepliesCollapsed]);

    const handleUpdate = updatedComment => {
        onCommentUpdate(prev =>
            prev.map(c => (c.id === updatedComment.id ? { ...c, text: updatedComment.text } : c))
        );
        setIsEditing(false);
    };

    const handleReply = newReply => {
        const enrichedComment = {
            ...newReply,
            userDisplayName: currentUser.name,
            userAvatarUrl: currentUser.profileImageUrl,  
        };
        onCommentUpdate(prev => [...prev, enrichedComment]);
        setShowReplyForm(false);
    };

    const handleDelete = async () => {
        try {
            await deleteComment(comment.id);
            onCommentUpdate(prev =>
                prev.map(c =>
                c.id === comment.id ? { ...c, text: '[deleted]', isDeleted: true } : c
                )
            );
        } catch (err) {
            console.error('Delete failed', err);
        }
    };

    const handleToggleUpvote = async () => {
        try {
            await toggleCommentUpvote(comment.id);
            
            const newUpvoters = hasUpvoted
                ? comment.upvoters.filter(id => id !== currentUser.id)
                : [...(comment.upvoters || []), currentUser.id];

            onCommentUpdate(prev =>
                prev.map(c =>
                    c.id === comment.id ? { ...c, upvoters: newUpvoters } : c
                )
            );
        } catch (error) {
            openSnackbar(error.message, 'error');
        }
    };

    return (
        <Box 
        sx={{ 
            ml: isNested ? 3 : 0,
            mb: 1,
            position: 'relative'
        }}
        >
        {/* Nested comment indicator line */}
        {isNested && (
            <Box
            sx={{
                position: 'absolute',
                left: -12,
                top: 0,
                bottom: 0,
                width: 2,
                bgcolor: 'divider',
                opacity: 0.3
            }}
            />
        )}

        <Box
            sx={{
            py: 1.5,
            px: 2,
            borderRadius: 5,
            bgcolor: depth % 2 === 0 ? '#fff' : 'transparent',
            }}
        >
            {/* Header */}
            <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
            <UserAvatar
                user={{ 
                    id: comment.userId, 
                    displayName: comment.userDisplayName, 
                    profileImageUrl: comment.userAvatarUrl 
                }}
                size={24}
                sx={{
                    border: '1px solid',
                    borderColor: 'divider'
                }}
            />
            <Typography 
                variant="subtitle2" 
                fontWeight={600}
                sx={{ fontSize: isMobile ? 14 : 13 }}
            >
                {comment.userDisplayName}
            </Typography>
            <Typography 
                variant="caption" 
                color="text.secondary"
                sx={{ fontSize: isMobile ? 12 : 11 }}
            >
                {new Date(comment.createdAt).toLocaleDateString()}
            </Typography>
            </Stack>

            {/* Content */}
            <Box ml={4}>
            {isEditing ? (
                <CommentForm
                    articleId={comment.articleId}
                    parentCommentId={comment.parentCommentId}
                    initialText={comment.text}
                    commentId={comment.id}
                    onSuccess={handleUpdate}
                    onCancel={() => setIsEditing(false)}
                />
            ) : (
                <Typography 
                    variant="body2" 
                    sx={{ 
                        fontSize: isMobile ? 14 : 13,
                        lineHeight: 1.4,
                        mb: 1
                    }}
                    >
                    {comment.isDeleted ? '[deleted]' : comment.text}
                </Typography>
            )}

            {/* Actions */}
            {!isEditing && !comment.isDeleted && (
                <CommentActions
                    isAuthor={isAuthor}
                    hasUpvoted={hasUpvoted}
                    isMobile={isMobile}
                    replies={replies}
                    repliesCollapsed={repliesCollapsed}
                    onToggleReplyForm={() => setShowReplyForm(prev => !prev)}
                    onToggleReplies={() => setRepliesCollapsed(prev => !prev)}
                    onToggleUpvote={handleToggleUpvote}
                    onEdit={() => setIsEditing(true)}
                    onDelete={() => setConfirmOpen(true)}
                    upvoteCount={comment.upvoters?.length || 0}
                />
            )}

            </Box>
        </Box>

        {/* Reply Form */}
        {showReplyForm && (
            <Box mt={2} ml={4}>
            <CommentForm
                articleId={comment.articleId}
                parentCommentId={comment.id}
                onSuccess={handleReply}
                onCancel={() => setShowReplyForm(false)}
                autoFocus={true}
            />
            </Box>
        )}

        {/* Replies */}
        <Collapse in={!repliesCollapsed}>
            <Box mt={1}>
            {replies.map(reply => (
                <CommentItem
                key={reply.id}
                comment={reply}
                allComments={allComments}
                currentUser={currentUser}
                onCommentUpdate={onCommentUpdate}
                depth={depth + 1}
                />
            ))}
            </Box>
        </Collapse>

        {/* Delete Confirmation */}
        <ConfirmationDialog
            open={confirmOpen}
            title="Delete Comment"
            content="This action cannot be undone. Are you sure you want to delete this comment?"
            onCancel={() => setConfirmOpen(false)}
            onConfirm={() => {
            setConfirmOpen(false);
            handleDelete();
            }}
            confirmText="Delete"
        />
        </Box>
    );
}